import { useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { collection, doc, onSnapshot } from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { RbacContext, type RbacContextValue } from "@/features/rbac/components/RbacContext";
import { rbacKeys } from "@/features/rbac/hooks/rbacKeys";
import { useAssignment } from "@/features/rbac/hooks/useAssignments";
import { useCurrentProfile } from "@/features/rbac/hooks/useCurrentProfile";
import { useRoles } from "@/features/rbac/hooks/useRoles";
import { BACKSTOP_ROLE_ID } from "@/features/rbac/permissions/systemRoles";
import { RBAC_ASSIGNMENTS_COLLECTION } from "@/features/rbac/services/assignmentsService";
import {
  RBAC_ROLES_COLLECTION,
  ensureSystemRoles,
} from "@/features/rbac/services/rolesService";
import { matches, matchesAll, matchesAny } from "@/features/rbac/utils/matches";
import { resolveEffectivePermissions } from "@/features/rbac/utils/resolvePermissions";

interface RbacProviderProps {
  children: ReactNode;
}

/**
 * Resuelve los permisos de la persona autenticada y los pone a disposición de
 * toda la aplicación.
 *
 * Es el único punto donde se leen roles y asignaciones. `can` acaba siendo una
 * consulta síncrona a un `Set` con identidad estable, así que se puede usar sin
 * miedo en dependencias de `useMemo` o en definiciones de columna de tabla.
 */
export function RbacProvider({ children }: RbacProviderProps): React.JSX.Element {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const uid = currentUser?.uid ?? null;

  const profileQuery = useCurrentProfile();
  const rolesQuery = useRoles();
  const assignmentQuery = useAssignment(uid);

  const subject = profileQuery.data ?? null;
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data]);
  const assignment = assignmentQuery.data ?? null;

  const isLoading =
    uid !== null &&
    (profileQuery.isLoading || rolesQuery.isLoading || assignmentQuery.isLoading);

  const effective = useMemo(
    () => resolveEffectivePermissions(subject, assignment, roles),
    [subject, assignment, roles]
  );

  // Los roles y la asignación propia se escuchan en vivo: si alguien edita un
  // rol mientras la persona está dentro, sus permisos cambian de inmediato en
  // vez de esperar a que caduque la caché.
  useEffect(() => {
    if (!uid) return;

    const unsubscribeRoles = onSnapshot(collection(db, RBAC_ROLES_COLLECTION), () => {
      void queryClient.invalidateQueries({ queryKey: rbacKeys.roles() });
    });

    const unsubscribeAssignment = onSnapshot(
      doc(db, RBAC_ASSIGNMENTS_COLLECTION, uid),
      () => {
        void queryClient.invalidateQueries({ queryKey: rbacKeys.assignment(uid) });
      }
    );

    return () => {
      unsubscribeRoles();
      unsubscribeAssignment();
    };
  }, [uid, queryClient]);

  // Siembra automática: la primera vez que entra un administrador heredado y la
  // colección está vacía, se crean los diez roles del sistema. Así el despliegue
  // no requiere ningún paso manual.
  const seedAttempted = useRef(false);
  useEffect(() => {
    if (seedAttempted.current) return;
    if (!uid || rolesQuery.isLoading || roles.length > 0) return;
    if (!subject) return;

    const isLegacySuperAdmin =
      subject.legacyType === "admin" ||
      subject.legacyPermissions.includes(BACKSTOP_ROLE_ID) ||
      subject.legacyPermissions.includes("center_admin");

    if (!isLegacySuperAdmin) return;

    seedAttempted.current = true;
    void ensureSystemRoles(uid)
      .then(() => queryClient.invalidateQueries({ queryKey: rbacKeys.roles() }))
      .catch((error) => {
        console.error("No se pudieron sembrar los roles del sistema:", error);
      });
  }, [uid, subject, roles.length, rolesQuery.isLoading, queryClient]);

  const value = useMemo<RbacContextValue>(() => {
    const granted = effective.permissions;

    return {
      can: (permission) => (isLoading ? false : matches(granted, permission)),
      canAny: (permissions) => (isLoading ? false : matchesAny(granted, permissions)),
      canAll: (permissions) => (isLoading ? false : matchesAll(granted, permissions)),
      permissions: granted,
      roleIds: effective.roleIds,
      roles,
      source: effective.source,
      subject,
      isLoading,
    };
  }, [effective, roles, subject, isLoading]);

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
}
